import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { openReceipt } from "../lib/receipts";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Receipt, Plus, Trash2, Paperclip, CheckCircle2, X, Send, RefreshCw, FileCheck2, ClipboardCheck, Download, FileText } from "lucide-react";
import ReceiptUploader from "../components/ReceiptUploader";
import CurrencySelect, { currencySymbol } from "../components/CurrencySelect";
import { exportCsv } from "../lib/exportCsv";

const statusBadge = (status, t) => {
  const map = {
    draft: ["sw-badge-neutral", t("status.draft")],
    pending: ["sw-badge-pending", t("status.pending")],
    approved: ["sw-badge-approved", t("status.approved")],
    rejected: ["sw-badge-rejected", t("status.rejected")],
    submitted_on_redteam: ["sw-badge-gold", t("status.submitted_on_redteam")],
    paid: ["sw-badge-approved", t("status.paid")],
  };
  const [cls, lbl] = map[status] || ["sw-badge-neutral", status];
  return <span className={`sw-badge ${cls}`}>{lbl}</span>;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Trigger a browser download for a Blob response (used by PDF exports).
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); window.URL.revokeObjectURL(url);
};

export default function Reimbursements() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isApprover = ["manager", "hr", "ceo", "admin"].includes(user?.role);
  const [tab, setTab] = useState("project");
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projectItems, setProjectItems] = useState([
    { item_type: "material", project_id: "", cost_code_id: "", supplier_id: "", date: "", amount: "", description: "", receipt_url: "" },
  ]);
  // Sprint 30 — single currency governs the entire project reimbursement.
  const [projectCurrency, setProjectCurrency] = useState("USD");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [detail, setDetail] = useState(null);
  // Sprint 30 — admin-only "Trash" view for soft-deleted reimbursements.
  const [showTrash, setShowTrash] = useState(false);

  const load = async () => {
    const url = (showTrash && user?.role === "admin") ? "/reimbursements/deleted" : "/reimbursements";
    const [reimb, projs, ccs, sups] = await Promise.all([
      api.get(url),
      api.get("/projects"),
      api.get("/qbo/selected-classes"),
      api.get("/qbo/selected-vendors"),
    ]);
    setItems(reimb.data);
    setProjects(projs.data);
    // QBO Classes → use as cost codes. Normalize {Id, Name} → {id, code, name, active:true}
    setCostCodes((ccs.data || []).map((c) => ({ id: String(c.Id), code: c.Name, name: c.Name, active: true })));
    // QBO vendors come as {Id, DisplayName} — normalize to {id, name, type:"both"} so existing UI works
    setSuppliers((sups.data || []).map((v) => ({ id: String(v.Id), name: v.DisplayName, type: "both" })));
    if (isApprover && !showTrash) {
      try {
        const r = await api.get("/reimbursements/pending");
        setPending(r.data || []);
      } catch { /* non-fatal */ }
    }
  };
  useEffect(() => { load(); }, [showTrash]); // eslint-disable-line

  const submitProject = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess("");
    try {
      const payload = {
        currency: (projectCurrency || "USD").toUpperCase(),
        items: projectItems.map((it) => ({
          ...it,
          amount: parseFloat(it.amount),
          currency: (projectCurrency || "USD").toUpperCase(),
        })),
      };
      await api.post("/reimbursements/project", payload);
      setProjectItems([{ item_type: "material", project_id: "", cost_code_id: "", supplier_id: "", date: "", amount: "", description: "", receipt_url: "" }]);
      setProjectCurrency("USD");
      setSuccess(`${payload.items.length} project cost item(s) submitted.`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const addItem = () =>
    setProjectItems([...projectItems, { item_type: "material", project_id: "", cost_code_id: "", supplier_id: "", date: "", amount: "", description: "", receipt_url: "" }]);

  const updateItem = (idx, field, value) => {
    const copy = [...projectItems];
    copy[idx] = { ...copy[idx], [field]: value };
    setProjectItems(copy);
  };

  const removeItem = (idx) => setProjectItems(projectItems.filter((_, i) => i !== idx));

  // Filter suppliers by item type
  const suppliersFor = (itemType) =>
    suppliers.filter((s) => s.type === "both" || s.type === itemType);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
            <Receipt size={22} />
          </div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("reimb.title")}</h1>
            <p className="text-[14px] text-[#747474]">{items.length} {t("reimb.entries")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reimb-export-csv"
            onClick={() => {
              const rows = items.map((r) => ({
                Date: r.created_at?.slice(0, 10) || "",
                User: r.user_name || "",
                Type: r.type || "",
                Category: r.category || "",
                Description: r.description || "",
                Currency: r.currency || "USD",
                Amount: r.amount,
                "USD amount": r.usd_amount ?? "",
                "FX rate": r.fx_rate ?? "",
                "Cost code": r.cost_code || "",
                Supplier: r.store || "",
                Project: r.project_id || "",
                Status: r.status,
                "Decision comment": r.decision_comment || r.rejection_reason || "",
              }));
              exportCsv(`reimbursements_${new Date().toISOString().slice(0, 10)}.csv`, rows);
            }}
            className="sw-btn-secondary"
          >
            <Download size={14} /> {t("common.export_csv")}
          </button>
          <button
            data-testid="reimb-export-pdf-bulk"
            onClick={async () => {
              try {
                const params = tab && tab !== "all" ? `?type=${tab}` : "";
                const { data } = await api.get(`/reimbursements/pdf/bulk${params}`, { responseType: "blob" });
                downloadBlob(data, `reimbursements_${new Date().toISOString().slice(0, 10)}.pdf`);
              } catch (e) { alert(e?.response?.data?.detail || t("common.error")); }
            }}
            className="sw-btn-secondary"
          >
            <FileText size={14} /> {t("common.export_pdf")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex bg-white border border-[#eeeded] rounded-full p-1 w-fit">
          <button data-testid="tab-project" onClick={() => setTab("project")} className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "project" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
            {t("reimb.tab_project")}
          </button>
          {isApprover && (
            <button data-testid="tab-approvals" onClick={() => setTab("approvals")} className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all flex items-center gap-1.5 ${tab === "approvals" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
              <ClipboardCheck size={13} /> {t("reimb.tab_approvals")}
              {pending.length > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#fef3c7] text-[#92400e] text-[10px] font-semibold px-1">{pending.length}</span>}
            </button>
          )}
        </div>
        {user?.role === "admin" && (
          <button
            data-testid="reimb-toggle-trash"
            onClick={() => setShowTrash((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 ${showTrash ? "bg-[#881a27] text-white" : "bg-white text-[#881a27] border border-[#f1c2c2]"}`}
          >
            <Trash2 size={12} /> {t("reimb.show_deleted")}
          </button>
        )}
      </div>

      {tab === "approvals" && isApprover && (
        <BatchDecidePanel
          pending={pending}
          onChanged={async () => { await load(); }}
        />
      )}

      <div className="text-[12px] text-[#747474] -mt-3">{t("reimb.tip_personal")}</div>

      {success && (
        <div className="bg-[#b8e6cd] text-[#0b430b] rounded-[12px] px-4 py-3 text-[13px] flex items-center gap-2">
          <CheckCircle2 size={16} /> {success}
        </div>
      )}

      {tab === "project" && (
        <form onSubmit={submitProject} className="flex flex-col gap-4">
          {/* Sprint 30 — one currency governs the entire project reimbursement.
              Every item is booked in this currency and converted to USD once,
              at approval time. */}
          <div className="sw-card !p-4 flex items-center gap-3 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">
              {t("reimb.request_currency") || "Currency (applies to all items)"} *
            </span>
            <div className="min-w-[200px]">
              <CurrencySelect
                value={projectCurrency}
                onChange={setProjectCurrency}
                testid="reimb-request-currency"
              />
            </div>
            <span className="text-[11px] text-[#94a3b8]">
              {t("reimb.request_currency_help") || "All items in this submission will be recorded in this currency."}
            </span>
          </div>
          {projectItems.map((it, idx) => (
            <div key={idx} className="sw-card flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-[#e2e8f0]">
                <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">Item {idx + 1}</span>
                {projectItems.length > 1 && (
                  <button
                    type="button"
                    data-testid={`remove-proj-item-${idx}`}
                    onClick={() => removeItem(idx)}
                    className="inline-flex items-center gap-1 text-[12px] text-[#881a27] hover:bg-rose-50 px-2 py-1 rounded-[8px] transition-colors"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="sw-label">{t("reimb.item_type")}</label>
                <select value={it.item_type} onChange={(e) => updateItem(idx, "item_type", e.target.value)} className="sw-input">
                  <option value="material">{t("reimb.material")}</option>
                  <option value="service">{t("reimb.service")}</option>
                </select>
              </div>
              <div>
                <label className="sw-label">{t("reimb.project")}</label>
                <select data-testid={`proj-project-${idx}`} value={it.project_id} onChange={(e) => updateItem(idx, "project_id", e.target.value)} className="sw-input" required>
                  <option value="">{t("reimb.select_project")}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="sw-label">{t("reimb.cost_code")}</label>
                <select data-testid={`proj-cc-${idx}`} value={it.cost_code_id} onChange={(e) => updateItem(idx, "cost_code_id", e.target.value)} className="sw-input" required>
                  <option value="">{t("reimb.select_cost_code")}</option>
                  {costCodes.map((c) => (
                    <option key={c.id} value={c.id}>{c.code}{c.description ? ` · ${c.description}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="sw-label">{t("reimb.supplier")}</label>
                <select data-testid={`proj-supplier-${idx}`} value={it.supplier_id} onChange={(e) => updateItem(idx, "supplier_id", e.target.value)} className="sw-input" required>
                  <option value="">{t("reimb.select_supplier")}</option>
                  {suppliersFor(it.item_type).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="sw-label">{t("reimb.date")}</label>
                <input type="date" value={it.date} onChange={(e) => updateItem(idx, "date", e.target.value)} className="sw-input" required />
              </div>
              <div>
                <label className="sw-label">{t("reimb.amount")}</label>
                <input data-testid={`proj-amount-${idx}`} type="number" step="0.01" value={it.amount} onChange={(e) => updateItem(idx, "amount", e.target.value)} className="sw-input w-full" required />
              </div>
              <div className="md:col-span-2">
                <label className="sw-label">{t("reimb.description")}</label>
                <input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="sw-input" required />
              </div>
              <div className="md:col-span-2">
                <label className="sw-label">{t("reimb.receipt")} *</label>
                <ReceiptUploader
                  value={it.receipt_url}
                  onChange={(url) => updateItem(idx, "receipt_url", url)}
                  testid={`proj-upload-${idx}`}
                />
              </div>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3 justify-between">
            <button type="button" onClick={addItem} className="sw-btn-secondary"><Plus size={14} /> {t("reimb.add_item")}</button>
            <button
              data-testid="project-submit"
              type="submit"
              disabled={submitting || projectItems.some((it) => !it.receipt_url)}
              className="sw-btn-primary"
            >
              {submitting ? t("common.loading") : t("reimb.submit")}
            </button>
          </div>
        </form>
      )}

      {/* History */}
      <div>
        <h2 className="sw-heading text-[18px] mb-4">{t("reimb.history")}</h2>
        {(() => {
          const myDrafts = items.filter((it) => it.status === "draft" && it.user_id === user?.id);
          if (myDrafts.length === 0) return null;
          return (
            <div className="mb-3 rounded-[14px] border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="reimb-drafts-summary">
              <div className="text-[13px] text-amber-900">
                {myDrafts.length === 1 ? t("drafts.summary_one") : t("drafts.summary_many", { n: myDrafts.length })}
              </div>
              <RefreshCw size={16} className="text-amber-700 shrink-0" />
            </div>
          );
        })()}
        {items.length === 0 ? (
          <div className="sw-card text-[#747474] text-center py-12">—</div>
        ) : (
          <div className="sw-card !p-0 overflow-x-auto">
            <table className="w-full text-[14px] min-w-[900px]">
              <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">{t("reimb.col_category")}</th>
                  <th className="text-left px-5 py-3">{t("reimb.col_description")}</th>
                  <th className="text-left px-5 py-3">{t("reimb.col_cost_code_supplier")}</th>
                  <th className="text-left px-5 py-3">{t("reimb.col_date")}</th>
                  <th className="text-right px-5 py-3">{t("reimb.col_amount")}</th>
                  <th className="text-center px-5 py-3">{t("reimb.col_receipt")}</th>
                  <th className="text-left px-5 py-3">{t("reimb.invoice")}</th>
                  <th className="text-left px-5 py-3">{t("reimb.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`reimb-row-${it.id}`}
                    onClick={() => setDetail(it)}
                    className="border-t border-[#eeeded] hover:bg-[#f6f6f6] cursor-pointer"
                  >
                    <td className="px-5 py-3 capitalize">{it.category}</td>
                    <td className="px-5 py-3 text-[#323232]">{it.description}</td>
                    <td className="px-5 py-3 text-[#747474] text-[12px]">
                      {it.cost_code && <div>{it.cost_code}</div>}
                      {it.store && <div className="text-[#475569]">{it.store}</div>}
                    </td>
                    <td className="px-5 py-3 text-[#323232]">{it.date}</td>
                    <td className="px-5 py-3 text-right font-medium">
                      <div>{currencySymbol(it.currency)}{parseFloat(it.amount).toFixed(2)}</div>
                      {it.currency && it.currency !== "USD" && it.usd_amount != null && (
                        <div className="text-[10px] text-[#94a3b8] font-normal">≈ ${parseFloat(it.usd_amount).toFixed(2)}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {it.receipt_url ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); openReceipt(it.receipt_url); }} className="text-[#475569] hover:underline inline-flex items-center gap-1 text-[12px]">
                          <Paperclip size={12} /> {t("reimb.view_receipt")}
                        </button>
                      ) : <span className="text-[#747474] text-[12px]">—</span>}
                    </td>
                    <td className="px-5 py-3">{it.invoice_number ? <span className="sw-badge sw-badge-gold">{it.invoice_number}</span> : <span className="text-[#747474] text-[12px]">—</span>}</td>
                    <td className="px-5 py-3">
                      {statusBadge(it.status, t)}
                      {it.status === "pending" && it.approver_name && (
                        <div data-testid="reimb-pending-approver" className="text-[10px] text-[#92400e] mt-1">
                          {t("travel.waiting_for")} <strong>{it.approver_name}</strong>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <ReimbDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onChanged={async () => { await load(); const fresh = (await api.get("/reimbursements")).data.find((r) => r.id === detail.id); setDetail(fresh || null); }}
        />
      )}
    </div>
  );
}

function ReimbDetailModal({ item, onClose, onChanged }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [approverName, setApproverName] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [redteamRef, setRedteamRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [logs, setLogs] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    date: item.date || "",
    amount: String(item.amount ?? ""),
    currency: item.currency || "USD",
    description: item.description || "",
    expense_type: item.expense_type || "",
    receipt_url: item.receipt_url || "",
  });
  const [resubmitting, setResubmitting] = useState(false);
  const [qboRetrying, setQboRetrying] = useState(false);

  useEffect(() => {
    if (item.decided_by) {
      api.get(`/users/${item.decided_by}`).then((r) => setApproverName(r.data?.name || "")).catch(() => {});
    }
  }, [item.decided_by]);

  const isOwner = user?.id === item.user_id;
  const qbo = item.qbo_refs || {};
  const isProject = item.category === "project";
  const canSubmitRedTeam = isOwner && isProject && item.status === "approved";
  const canEditDraft = isOwner && item.status === "draft";
  // Sprint 30 — delete rights: draft-owner OR admin (any status).
  const isAdmin = user?.role === "admin";
  const canDelete = (canEditDraft || isAdmin) && !item.deleted_at;

  const deleteReimb = async () => {
    const msg = isAdmin && !canEditDraft
      ? (t("reimb.confirm_admin_delete") || "This will move the reimbursement to the deleted trash. Only admins can restore it. Continue?")
      : (t("reimb.confirm_delete") || "Delete this draft?");
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/reimbursements/${item.id}`);
      if (onChanged) await onChanged();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const buildEditPayload = () => {
    const payload = {
      date: editForm.date,
      amount: parseFloat(editForm.amount),
      currency: editForm.currency,
      description: editForm.description || null,
      receipt_url: editForm.receipt_url || null,
    };
    if (!isProject && editForm.expense_type) payload.expense_type = editForm.expense_type;
    return payload;
  };

  const saveDraft = async () => {
    if (!editForm.date || !editForm.amount) {
      alert(t("reimb.date_value_required"));
      return;
    }
    setResubmitting(true);
    try {
      await api.post(`/reimbursements/${item.id}/save-draft`, buildEditPayload());
      setEditMode(false);
      if (onChanged) await onChanged();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao salvar rascunho");
    } finally {
      setResubmitting(false);
    }
  };

  const resubmit = async () => {
    if (!editForm.date || !editForm.amount) {
      alert(t("reimb.date_value_required"));
      return;
    }
    setResubmitting(true);
    try {
      await api.post(`/reimbursements/${item.id}/resubmit`, buildEditPayload());
      setEditMode(false);
      if (onChanged) await onChanged();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao ressubmeter");
    } finally {
      setResubmitting(false);
    }
  };

  const submitRedTeam = async () => {
    setSubmitting(true);
    try {
      await api.post(`/reimbursements/${item.id}/submit-redteam`, { redteam_reference: redteamRef || null });
      setShowSubmit(false);
      setRedteamRef("");
      if (onChanged) await onChanged();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error submitting");
    } finally {
      setSubmitting(false);
    }
  };

  const loadLogs = async () => {
    const r = await api.get(`/reimbursements/${item.id}/qbo-logs`);
    setLogs(r.data);
    setShowLogs(true);
  };

  const paymentBadge = () => {
    const s = qbo.payment_status;
    if (s === "paid") return <span className="sw-badge sw-badge-approved">{t("reimb.qbo_paid")}</span>;
    if (s === "partial") return <span className="sw-badge sw-badge-pending">{t("reimb.qbo_partial")}</span>;
    if (s === "unpaid") return <span className="sw-badge sw-badge-pending">{t("reimb.qbo_unpaid")}</span>;
    return <span className="text-[12px] text-[#94a3b8]">—</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="reimb-detail-modal" className="bg-white rounded-[20px] max-w-[640px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold">{item.category || "reimbursement"}</div>
            <h3 className="sw-heading text-[22px] mt-1">{currencySymbol(item.currency)}{parseFloat(item.amount).toFixed(2)}</h3>
            {item.currency && item.currency !== "USD" && item.usd_amount != null && (
              <div className="text-[12px] text-[#475569] mt-1" data-testid="reimb-fx-line">
                ≈ <span className="font-semibold">${parseFloat(item.usd_amount).toFixed(2)}</span>
                <span className="text-[10.5px] text-[#94a3b8]"> · rate {parseFloat(item.fx_rate).toFixed(4)} ({item.fx_date})</span>
              </div>
            )}
            <div className="text-[13px] text-[#747474] italic mt-1">{item.description || "—"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`reimb-detail-pdf-${item.id}`}
              onClick={async () => {
                try {
                  const { data } = await api.get(`/reimbursements/${item.id}/pdf`, { responseType: "blob" });
                  downloadBlob(data, `reimbursement-${(item.invoice_number || item.id).slice(0, 20)}.pdf`);
                } catch (e) { alert(e?.response?.data?.detail || t("common.error")); }
              }}
              className="text-[#475569] hover:text-[#1e293b] flex items-center gap-1 text-[12px]"
              title={t("common.download_pdf")}
            >
              <FileText size={14} /> PDF
            </button>
            <button onClick={onClose} className="text-[#94a3b8]" data-testid="reimb-detail-close"><X size={20} /></button>
          </div>
        </div>

        {/* Sprint 30 — delete row (owner-draft or admin any status) */}
        {canDelete && !editMode && (
          <div className="flex justify-end pt-2">
            <button
              data-testid="reimb-delete-btn"
              onClick={deleteReimb}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#f1c2c2] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#881a27] hover:bg-[#fef2f2]"
            >
              <Trash2 size={13} />
              {isAdmin && !canEditDraft
                ? (t("reimb.admin_delete_btn") || "Delete (admin)")
                : (t("common.delete") || "Delete")}
            </button>
          </div>
        )}

        {/* Sprint 30 — admin restore banner for soft-deleted reimbursements. */}
        {item.deleted_at && isAdmin && (
          <div className="mt-4 rounded-[14px] border border-rose-200 bg-rose-50 p-4" data-testid="reimb-deleted-banner">
            <div className="flex items-start gap-3">
              <Trash2 size={16} className="text-rose-700 mt-0.5" />
              <div className="flex-1">
                <div className="text-[12px] uppercase tracking-[0.1em] font-semibold text-rose-900">{t("common.deleted") || "Deleted"}</div>
                <div className="text-[12px] text-rose-800 mt-0.5">
                  <strong>{item.deleted_by_name}</strong> ({item.deleted_by_role}) · {item.deleted_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <button
                data-testid="reimb-restore-btn"
                onClick={async () => {
                  try {
                    await api.post(`/reimbursements/${item.id}/restore`);
                    if (onChanged) await onChanged();
                    onClose();
                  } catch (e) { alert(e?.response?.data?.detail || "Error"); }
                }}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-white border border-rose-300 px-3 py-1.5 text-[12px] font-semibold text-rose-900 hover:bg-rose-100"
              >
                <RefreshCw size={13} /> {t("travel.restore") || "Restore"}
              </button>
            </div>
          </div>
        )}

        <div className="text-[13px] grid grid-cols-2 gap-x-4 gap-y-2 py-3 border-t border-[#f1f5f9]">
          <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider block">{t("common.date")}</span>{item.date}</div>
          {item.cost_code && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider block">{t("common.cost_code")}</span>{item.cost_code}</div>}
          {item.store && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider block">Supplier</span>{item.store}</div>}
          {item.invoice_number && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider block">Invoice #</span>{item.invoice_number}</div>}
          <div className="col-span-2 pt-2 flex items-center gap-2 flex-wrap">
            <span className="text-[#94a3b8] text-[11px] uppercase tracking-wider">{t("common.status")}</span>
            {statusBadge(item.status, t)}
            {item.status === "pending" && item.approver_name && (
              <span className="text-[11px] text-[#92400e]">
                · {t("travel.waiting_for")} <strong>{item.approver_name}</strong>
              </span>
            )}
          </div>
        </div>

        {(item.decision_comment || item.decided_at) && (
          <div className="mt-3 rounded-[14px] border border-[#e2e8f0] bg-[#f8fafc] p-4">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold mb-2">{t("common.approval_trail")}</div>
            {approverName && <div className="text-[13px] text-[#1e293b]"><strong>{approverName}</strong> <span className="text-[#94a3b8]">·</span> {item.status}</div>}
            {item.decided_at && <div className="text-[11px] text-[#94a3b8] mt-0.5">{item.decided_at.slice(0,16).replace("T"," ")}</div>}
            {item.decision_comment && <div className="text-[13px] text-[#475569] italic mt-2">&ldquo;{item.decision_comment}&rdquo;</div>}
          </div>
        )}

        {item.receipt_url && !editMode && (
          <button type="button" onClick={() => openReceipt(item.receipt_url)} className="mt-4 inline-flex items-center gap-2 text-[#475569] text-[13px] hover:underline">
            <Paperclip size={14} /> Open receipt
          </button>
        )}

        {/* Sprint 28.2 — Draft owner can edit & resubmit (typically spawned from batch-decide rejection) */}
        {canEditDraft && (
          <div className="mt-5 rounded-[14px] border border-amber-300 bg-amber-50 p-4" data-testid="reimb-draft-owner-banner">
            {!editMode ? (
              <div className="flex items-start gap-3">
                <RefreshCw size={18} className="text-amber-700 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-amber-900">{t("reimb.draft_returned_title")}</div>
                  <p className="text-[12px] text-amber-800 mt-0.5">
                    {(item.rejection_reason || item.decision_comment) ? <>{t("reimb.manager_comment")}: <em>&ldquo;{item.rejection_reason || item.decision_comment}&rdquo;</em>. </> : null}
                    {t("reimb.draft_returned_help")}
                  </p>
                </div>
                <button data-testid="reimb-edit-draft-btn" onClick={() => setEditMode(true)} className="sw-btn-primary">{t("reimb.edit_draft")}</button>
              </div>
            ) : (
              <div data-testid="reimb-edit-form">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[13px] font-semibold text-amber-900">{t("reimb.editing_draft")}</div>
                  <button onClick={() => setEditMode(false)} data-testid="reimb-cancel-edit-btn" className="text-[12px] text-amber-800 hover:underline">{t("common.cancel")}</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="sw-label">{t("reimb.date")}</label>
                    <input data-testid="reimb-edit-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="sw-input" />
                  </div>
                  <div>
                    <label className="sw-label">{t("reimb.amount")}</label>
                    <div className="flex gap-1.5">
                      <input data-testid="reimb-edit-amount" type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="sw-input flex-1" />
                      <CurrencySelect
                        value={editForm.currency}
                        onChange={(v) => setEditForm({ ...editForm, currency: v })}
                        testid="reimb-edit-currency"
                        className="!flex-none !w-[120px] !text-[11px]"
                      />
                    </div>
                  </div>
                  {!isProject && (
                    <div className="col-span-2">
                      <label className="sw-label">{t("reimb.expense_type")}</label>
                      <input data-testid="reimb-edit-expense-type" value={editForm.expense_type} onChange={(e) => setEditForm({ ...editForm, expense_type: e.target.value })} className="sw-input" />
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="sw-label">{t("reimb.description")}</label>
                    <input data-testid="reimb-edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="sw-input" />
                  </div>
                  {isProject && (
                    <div className="col-span-2 text-[11px] text-amber-800 bg-white/50 rounded-[8px] p-2 border border-amber-200">
                      {t("reimb.project_locked_warning")} <code>{item.cost_code || "—"}</code> · <code>{item.store || "—"}</code>
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="sw-label">{t("reimb.receipt")}</label>
                    <ReceiptUploader
                      value={editForm.receipt_url}
                      onChange={(url) => setEditForm({ ...editForm, receipt_url: url })}
                      testid="reimb-edit-receipt"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2 pt-3 border-t border-amber-200">
                  <button onClick={() => setEditMode(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
                  <button
                    type="button"
                    data-testid="reimb-save-draft-btn"
                    onClick={saveDraft}
                    disabled={resubmitting}
                    className="sw-btn-secondary"
                    title={t("reimb.save_for_later_hint")}
                  >
                    {resubmitting ? "..." : t("reimb.save_for_later")}
                  </button>
                  <button data-testid="reimb-resubmit-btn" onClick={resubmit} disabled={resubmitting} className="sw-btn-primary disabled:opacity-50">
                    {resubmitting ? t("reimb.resubmitting") : t("reimb.resubmit")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* RedTeam submit (only owner of approved project reimb) */}
        {canSubmitRedTeam && !showSubmit && (
          <div className="mt-5 rounded-[14px] border border-[#dac28a] bg-[#fcf7eb] p-4">
            <div className="flex items-start gap-3">
              <FileCheck2 size={20} className="text-[#a38d5b] mt-0.5" />
              <div className="flex-1">
                <div className="text-[13.5px] font-semibold text-[#1e293b]">{t("reimb.next_step_redteam")}</div>
                <p className="text-[12px] text-[#475569] mt-1">{t("reimb.submit_redteam_help")} {t("reimb.redteam_after")}</p>
              </div>
              <button
                data-testid="submit-redteam-btn"
                onClick={() => setShowSubmit(true)}
                className="sw-btn-primary text-[12.5px] px-3 py-2"
              >
                <Send size={14} /> {t("reimb.submit_redteam")}
              </button>
            </div>
          </div>
        )}
        {canSubmitRedTeam && showSubmit && (
          <div className="mt-5 rounded-[14px] border border-[#cbd5e1] p-4">
            <label className="sw-label">{t("reimb.submit_redteam_ref")}</label>
            <input
              data-testid="redteam-ref-input"
              value={redteamRef}
              onChange={(e) => setRedteamRef(e.target.value)}
              placeholder="ex: RT-2026-001"
              className="sw-input"
            />
            <div className="flex gap-2 mt-3">
              <button
                data-testid="confirm-redteam-btn"
                onClick={submitRedTeam}
                disabled={submitting}
                className="sw-btn-primary"
              >
                {submitting ? t("common.loading") : t("reimb.submit_redteam_action")}
              </button>
              <button onClick={() => setShowSubmit(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
            </div>
          </div>
        )}

        {/* QBO sync info */}
        {qbo.sync_status && (
          <div className="mt-5 rounded-[14px] border border-[#cbd5e1] bg-[#f8fafc] p-4" data-testid="reimb-qbo-block">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[12px] uppercase tracking-[0.12em] text-[#475569] font-semibold">{t("reimb.qbo_sync_title")}</div>
              {qbo.sync_status === "synced" && paymentBadge()}
              {qbo.sync_status === "error" && <span className="sw-badge sw-badge-rejected">{t("common.sync_error")}</span>}
              {qbo.sync_status === "not_configured" && <span className="text-[11px] text-[#94a3b8]">{t("common.qbo_not_configured")}</span>}
            </div>
            {qbo.sync_status === "synced" && (
              <>
                <div className="text-[12.5px] grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("reimb.qbo_vendor_credit")}</span><code className="text-[11.5px] text-[#1e293b]">{qbo.vendor_credit_id}</code></div>
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("reimb.qbo_bill")}</span><code className="text-[11.5px] text-[#1e293b]">{qbo.employee_bill_id}</code></div>
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">Vendor (QBO)</span><code className="text-[11.5px]">{qbo.vendor_qbo_id}</code></div>
                  <div><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("reimb.employee_vendor_qbo")}</span><code className="text-[11.5px]">{qbo.employee_vendor_qbo_id}</code></div>
                  {qbo.synced_at && <div className="col-span-2"><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("reimb.qbo_synced_at")}</span>{qbo.synced_at.slice(0,16).replace("T"," ")}</div>}
                  {qbo.last_payment_check_at && <div className="col-span-2"><span className="text-[#94a3b8] text-[10px] uppercase tracking-wider block">{t("common.last_payment_check")}</span>{qbo.last_payment_check_at.slice(0,16).replace("T"," ")}</div>}
                </div>
                {["hr", "admin"].includes(user?.role) && qbo.payment_status !== "paid" && qbo.payment_status !== "partial" && (
                  <div className="mt-3 pt-3 border-t border-[#e5e7eb] flex justify-end">
                    <button
                      data-testid={`reimb-qbo-reopen-${item.id}`}
                      disabled={qboRetrying}
                      onClick={async () => {
                        if (!window.confirm(t("reimb.reopen_confirm"))) return;
                        setQboRetrying(true);
                        try {
                          await api.post(`/reimbursements/${item.id}/reopen`);
                          alert(t("reimb.reopen_ok"));
                          await onChanged();
                          onClose && onClose();
                        } catch (e) {
                          alert(e?.response?.data?.detail || t("common.error"));
                        } finally { setQboRetrying(false); }
                      }}
                      className="sw-btn-secondary text-[12px] flex items-center gap-1.5 !text-[#881a27] !border-[#e8b4b4]"
                      title={t("reimb.reopen_hint")}
                    >
                      <RefreshCw size={13} className={qboRetrying ? "animate-spin" : ""} />
                      {qboRetrying ? "…" : t("reimb.reopen_from_qbo")}
                    </button>
                  </div>
                )}
              </>
            )}
            {qbo.sync_status === "error" && (
              <>
                <div className="text-[12px] text-[#881a27] mt-1 break-words font-mono bg-rose-50 p-2 rounded">{qbo.last_error}</div>
                {["hr", "admin"].includes(user?.role) && (
                  <div className="mt-2 flex justify-end">
                    <button
                      data-testid={`reimb-qbo-retry-${item.id}`}
                      disabled={qboRetrying}
                      onClick={async () => {
                        setQboRetrying(true);
                        try {
                          await api.post(`/reimbursements/${item.id}/qbo-resync`);
                          let settled = false;
                          for (let i = 0; i < 40; i++) {
                            await new Promise((r) => setTimeout(r, 3000));
                            const { data } = await api.get(`/reimbursements/${item.id}/qbo-status`);
                            if (data.sync_status === "synced") {
                              alert(t("reimb.qbo_resync_ok"));
                              settled = true;
                              break;
                            }
                            if (data.sync_status === "error") {
                              alert(data.last_error || t("common.error"));
                              settled = true;
                              break;
                            }
                          }
                          if (!settled) alert(t("reimb.qbo_resync_bg"));
                          await onChanged();
                        } catch (e) {
                          alert(e?.response?.data?.detail || t("common.error"));
                        } finally { setQboRetrying(false); }
                      }}
                      className="sw-btn-secondary text-[12px] flex items-center gap-1.5"
                    >
                      <RefreshCw size={13} className={qboRetrying ? "animate-spin" : ""} />
                      {qboRetrying ? t("reimb.qbo_resync_running") : t("reimb.qbo_retry_sync")}
                    </button>
                  </div>
                )}
              </>
            )}
            {qbo.environment === "sandbox" && (qbo.vendor_credit_id || "").startsWith("DRYRUN-") && (
              <div className="text-[10.5px] text-[#a38d5b] mt-3 uppercase tracking-[0.12em] font-semibold">{t("reimb.dry_run_warning")}</div>
            )}
            <button data-testid="qbo-logs-btn" onClick={loadLogs} className="sw-btn-secondary mt-3 text-[12px]">
              <RefreshCw size={12} /> {t("reimb.qbo_logs")}
            </button>
          </div>
        )}

        {/* History timeline */}
        {Array.isArray(item.history_log) && item.history_log.length > 0 && (
          <div className="mt-5 rounded-[14px] border border-[#e2e8f0] bg-white p-4">
            <div className="text-[12px] uppercase tracking-[0.12em] text-[#475569] font-semibold mb-3">{t("reimb.history_log")}</div>
            <ol className="space-y-2.5">
              {item.history_log.map((h, i) => (
                <li key={i} className="flex gap-3 text-[12.5px]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#475569] mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <strong className="text-[#1e293b]">{h.event.replaceAll("_", " ")}</strong>
                      <span className="text-[10.5px] text-[#94a3b8]">{(h.timestamp || "").slice(0,16).replace("T"," ")}</span>
                    </div>
                    {h.details && <div className="text-[#475569] mt-0.5 break-words">{h.details}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* QBO Logs viewer */}
        {showLogs && (
          <div className="mt-4 rounded-[14px] border border-[#cbd5e1] bg-[#0f172a] text-[#cbd5e1] p-3 text-[11px] font-mono max-h-[260px] overflow-y-auto" data-testid="qbo-logs-block">
            {(logs || []).length === 0 && <div>{t("reimb.no_qbo_log")}</div>}
            {(logs || []).map((l) => (
              <div key={l.id} className="border-b border-[#1e293b] py-1.5">
                <div>
                  <span className={l.success ? "text-[#86efac]" : "text-[#fda4af]"}>[{l.response_status}]</span>{" "}
                  <span className="text-white">{l.method}</span> {l.path}{" "}
                  {l.dry_run && <span className="text-[#fcd34d]">(dry-run)</span>}
                </div>
                <div className="text-[9.5px] text-[#94a3b8]">{(l.timestamp || "").slice(0,19).replace("T"," ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------- Batch Decide Panel ----------------
function BatchDecidePanel({ pending, onChanged }) {
  const { t } = useI18n();
  // Track per-id local decisions: { [id]: "approved" | "rejected" | undefined }
  const [decisions, setDecisions] = useState({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const selectedIds = Object.keys(decisions).filter((id) => decisions[id]);
  const approveCount = selectedIds.filter((id) => decisions[id] === "approved").length;
  const rejectCount = selectedIds.filter((id) => decisions[id] === "rejected").length;
  const total = pending
    .filter((p) => selectedIds.includes(p.id))
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const setOne = (id, decision) => {
    setDecisions((d) => ({ ...d, [id]: decision }));
  };

  const markAll = (decision) => {
    const next = {};
    pending.forEach((p) => { next[p.id] = decision; });
    setDecisions(next);
  };

  const clear = () => { setDecisions({}); setComment(""); setResult(null); };

  const submit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        decisions: selectedIds.map((id) => ({ item_id: id, decision: decisions[id], comment })),
        comment,
      };
      const { data } = await api.post("/reimbursements/batch-decide", payload);
      setResult(data);
      setDecisions({});
      await onChanged();
    } catch (err) {
      alert(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (pending.length === 0) {
    return (
      <div className="sw-card text-center py-12" data-testid="batch-decide-empty">
        <ClipboardCheck className="mx-auto mb-3 text-[#cbd5e1]" size={36} />
        <div className="text-[14px] text-[#747474]">{t("batch.empty_state")}</div>
      </div>
    );
  }

  return (
    <div className="sw-card !p-5 flex flex-col gap-4" data-testid="batch-decide-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="sw-heading text-[18px]">{t("batch.title")}</h2>
          <p className="text-[12px] text-[#747474]">{t("batch.help")}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="batch-approve-all" onClick={() => markAll("approved")} className="text-[12px] px-3 py-1.5 rounded-[10px] bg-[#dcfce7] text-[#0b430b] hover:bg-[#bbf7d0]">{t("batch.approve_all")}</button>
          <button data-testid="batch-reject-all" onClick={() => markAll("rejected")} className="text-[12px] px-3 py-1.5 rounded-[10px] bg-[#fee2e2] text-[#881a27] hover:bg-[#fecaca]">{t("batch.reject_all")}</button>
          <button onClick={clear} className="text-[12px] px-3 py-1.5 rounded-[10px] bg-[#f6f6f6] text-[#747474]">{t("batch.clear")}</button>
        </div>
      </div>

      <div className="border border-[#eeeded] rounded-[12px] overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5">{t("batch.col_user")}</th>
              <th className="text-left px-3 py-2.5">{t("batch.col_description")}</th>
              <th className="text-left px-3 py-2.5">{t("batch.col_date")}</th>
              <th className="text-right px-3 py-2.5">Valor</th>
              <th className="text-center px-3 py-2.5">{t("batch.col_decision")}</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((p) => {
              const d = decisions[p.id];
              return (
                <tr key={p.id} data-testid={`batch-row-${p.id}`} className={`border-t border-[#eeeded] ${d === "approved" ? "bg-[#f0fdf4]" : d === "rejected" ? "bg-[#fef2f2]" : ""}`}>
                  <td className="px-3 py-2.5 font-medium text-[#1e293b]">{p.user_name || p.user_id}</td>
                  <td className="px-3 py-2.5 text-[#475569]">{p.description || "—"}</td>
                  <td className="px-3 py-2.5 text-[#747474]">{p.date}</td>
                  <td className="px-3 py-2.5 text-right font-semibold">
                    {currencySymbol(p.currency)}{parseFloat(p.amount).toFixed(2)}
                    {p.currency && p.currency !== "USD" && (
                      <div className="text-[9.5px] text-[#94a3b8] font-normal">{p.currency}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1 justify-center">
                      <button
                        data-testid={`batch-approve-${p.id}`}
                        onClick={() => setOne(p.id, "approved")}
                        className={`px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition ${d === "approved" ? "bg-[#008932] text-white" : "bg-white border border-[#d1d5db] text-[#475569] hover:border-[#008932]"}`}
                      >
                        {t("batch.approve")}
                      </button>
                      <button
                        data-testid={`batch-reject-${p.id}`}
                        onClick={() => setOne(p.id, "rejected")}
                        className={`px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition ${d === "rejected" ? "bg-[#881a27] text-white" : "bg-white border border-[#d1d5db] text-[#475569] hover:border-[#881a27]"}`}
                      >
                        {t("batch.reject")}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <label className="sw-label">{t("batch.comment_label")}</label>
        <input
          data-testid="batch-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="sw-input"
          placeholder={t("batch.comment_placeholder")}
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#eeeded]">
        <div className="text-[12.5px] text-[#475569]">
          {selectedIds.length === 0 ? (
            <span className="text-[#747474]">{t("batch.empty_help")}</span>
          ) : (
            <span className="font-medium">{t("batch.selected_summary", { a: approveCount, r: rejectCount, total: total.toFixed(2) })}</span>
          )}
        </div>
        <button
          data-testid="batch-submit"
          onClick={submit}
          disabled={submitting || selectedIds.length === 0}
          className="sw-btn-primary disabled:opacity-50"
        >
          {submitting ? t("batch.sending") : t("batch.submit", { n: selectedIds.length })}
        </button>
      </div>

      {result && (
        <div className="rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-4 text-[12.5px]" data-testid="batch-result">
          <div className="font-semibold text-[#1e293b] mb-1">{t("batch.processed_title")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] text-[#475569]">
            <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("batch.processed_approved")}</span>{result.approved_ids?.length || 0}</div>
            <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("batch.processed_rejected")}</span>{result.rejected_ids?.length || 0}</div>
            <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("batch.processed_drafts")}</span>{result.spawned_drafts?.length || 0}</div>
            <div><span className="block text-[10px] uppercase tracking-wider text-[#94a3b8]">{t("batch.processed_errors")}</span>{result.errors?.length || 0}</div>
          </div>
          {(result.errors || []).length > 0 && (
            <ul className="mt-2 text-[11px] text-[#881a27] space-y-1">
              {result.errors.map((e, i) => <li key={i}>• {e.id}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
